import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import {
  greeting,
  projectsHeader,
  publications,
  publicationsHeader,
} from "../../portfolio.js";
import ProjectsData from "../../shared/opensource/projects.json";
import "./Projects.css";

const FILTERS = [
  { id: "all", label: "All signals" },
  { id: "agent", label: "Agent systems" },
  { id: "data", label: "Data systems" },
  { id: "interface", label: "Interfaces" },
  { id: "lab", label: "Labs" },
];

const PROJECTS = ProjectsData.data || [];

function getProjectProfile(repo) {
  const source = `${repo.name} ${repo.description || ""}`.toLowerCase();

  if (/mcp|adaptimind|voice|speech|diarization|langchain/.test(source)) {
    return {
      id: "agent",
      label: "Agent systems",
      code: "NEURAL",
      accent: "cyan",
      short: "reasoning + tools",
    };
  }

  if (/crypto|stock|dashboard|frontend/.test(source)) {
    return {
      id: "interface",
      label: "Interfaces",
      code: "SURFACE",
      accent: "violet",
      short: "signal + interface",
    };
  }

  if (/sensor|generator|monitor|scrap|pipeline|calendar/.test(source)) {
    return {
      id: "data",
      label: "Data systems",
      code: "FLOW",
      accent: "orange",
      short: "capture + movement",
    };
  }

  return {
    id: "lab",
    label: "Labs",
    code: "FIELD",
    accent: "green",
    short: "curiosity + craft",
  };
}

function Arrow({ diagonal = false }) {
  return (
    <span
      className={
        diagonal ? "archive-arrow archive-arrow-diagonal" : "archive-arrow"
      }
    >
      {diagonal ? "↗" : "→"}
    </span>
  );
}

function RepositoryIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 3.75h10l4 4V20.25H5z" />
      <path d="M15 3.75v4h4M8.25 11h7.5M8.25 14.5h7.5" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m12 3.8 2.53 5.13 5.66.83-4.1 4 0.97 5.64L12 16.74 6.94 19.4l.97-5.64-4.1-4 5.66-.83z" />
    </svg>
  );
}

function ForkIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="6.25" cy="5.5" r="2.2" />
      <circle cx="17.75" cy="5.5" r="2.2" />
      <circle cx="12" cy="18.5" r="2.2" />
      <path d="M6.25 7.7v2.05A4.25 4.25 0 0 0 10.5 14h3a4.25 4.25 0 0 0 4.25-4.25V7.7M12 14v2.3" />
    </svg>
  );
}

function Orbit({ profile, compact = false }) {
  return (
    <div
      className={`signal-orbit signal-orbit-${profile.accent} ${
        compact ? "signal-orbit-compact" : ""
      }`}
      aria-hidden="true"
    >
      <span className="orbit-ring orbit-ring-one"></span>
      <span className="orbit-ring orbit-ring-two"></span>
      <span className="orbit-ring orbit-ring-three"></span>
      <span className="orbit-core">
        <i></i>
      </span>
      <span className="orbit-node orbit-node-one"></span>
      <span className="orbit-node orbit-node-two"></span>
      <span className="orbit-node orbit-node-three"></span>
      <b>{profile.code}</b>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "Recently";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

function ProjectCard({ repo, active, onSelect, index }) {
  const profile = getProjectProfile(repo);
  const languages = (repo.languages || [])
    .map((language) => language.name)
    .filter(Boolean);
  const tags = languages.length
    ? languages.slice(0, 3)
    : [repo.primaryLanguage || "Build log"];

  return (
    <article
      className={`archive-project-card archive-project-card-${profile.accent} ${
        active ? "is-active" : ""
      }`}
    >
      <button
        type="button"
        className="archive-project-select"
        onClick={() => onSelect(repo)}
        aria-pressed={active}
        aria-label={`Inspect ${repo.name}`}
      >
        <div className="project-card-topline">
          <span>{String(index + 1).padStart(2, "0")}</span>
          <span>
            {profile.code} / {profile.label}
          </span>
        </div>
        <div className="project-card-heading">
          <div className="project-card-icon">
            <RepositoryIcon />
          </div>
          <h2>{repo.name}</h2>
        </div>
        <p className="project-card-description">
          {repo.description || "A work-in-progress field experiment."}
        </p>
        <div className="project-card-visual">
          <Orbit profile={profile} compact />
          <span>{profile.short}</span>
        </div>
      </button>
      <div className="project-card-bottom">
        <div className="project-tags">
          {tags.map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
        <a
          href={repo.url}
          target="_blank"
          rel="noopener noreferrer"
          className="project-open-link"
          aria-label={`Open ${repo.name} on GitHub`}
        >
          View code <Arrow diagonal />
        </a>
      </div>
    </article>
  );
}

function ProjectInspector({ repo }) {
  const profile = getProjectProfile(repo);
  const languages = (repo.languages || [])
    .map((language) => language.name)
    .filter(Boolean);
  const tags = languages.length
    ? languages
    : [repo.primaryLanguage || "Source code"];

  return (
    <aside
      className={`project-inspector project-inspector-${profile.accent}`}
      aria-live="polite"
    >
      <div className="inspector-topline">
        <span>
          <i></i> Signal inspector
        </span>
        <span>
          {profile.code}_0
          {Math.max(1, PROJECTS.findIndex((item) => item.id === repo.id) + 1)}
        </span>
      </div>
      <div className="inspector-main">
        <Orbit profile={profile} />
        <div className="inspector-copy">
          <p>{profile.label}</p>
          <h2>{repo.name}</h2>
          <span className="inspector-language">
            <i></i>
            {repo.primaryLanguage || "Multi-language"}
          </span>
        </div>
      </div>
      <p className="inspector-description">
        {repo.description || "No field notes recorded yet."}
      </p>
      <div className="inspector-metrics">
        <div>
          <span>
            <StarIcon /> Stars
          </span>
          <b>{repo.stargazerCount || 0}</b>
        </div>
        <div>
          <span>
            <ForkIcon /> Forks
          </span>
          <b>{repo.forkCount || 0}</b>
        </div>
        <div>
          <span>LAST SIGNAL</span>
          <b>{formatDate(repo.updatedAt || repo.createdAt)}</b>
        </div>
      </div>
      <div className="inspector-tags">
        {tags.slice(0, 5).map((tag) => (
          <span key={tag}>{tag}</span>
        ))}
      </div>
      <a
        href={repo.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inspector-action"
      >
        Open the repository <Arrow diagonal />
      </a>
    </aside>
  );
}

function ResearchCard({ publication, index }) {
  const isPatent = /patent/i.test(publication.name);
  return (
    <article
      className={`research-record ${
        isPatent ? "research-record-patent" : "research-record-paper"
      }`}
    >
      <div className="research-record-kind">
        <span>0{index + 1}</span>
        <span>{isPatent ? "Patent record" : "Published research"}</span>
      </div>
      <div className="research-record-glyph" aria-hidden="true">
        {isPatent ? (
          <div className="braille-record">
            {[...Array(12)].map((_, dot) => (
              <i
                className={[0, 1, 3, 5, 7, 8].includes(dot) ? "raised" : ""}
                key={dot}
              ></i>
            ))}
          </div>
        ) : (
          <div className="paper-record">
            <i></i>
            <i></i>
            <i></i>
            <b>IEEE</b>
          </div>
        )}
      </div>
      <div className="research-record-copy">
        <span>{formatDate(publication.createdAt)}</span>
        <h3>{publication.name}</h3>
        <p>{publication.description}</p>
      </div>
      <a
        href={publication.url}
        target="_blank"
        rel="noopener noreferrer"
        className="research-record-link"
      >
        View record <Arrow diagonal />
      </a>
    </article>
  );
}

export default function Projects(props) {
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(
    PROJECTS[0] ? PROJECTS[0].id : null
  );
  const [scrollProgress, setScrollProgress] = useState(0);
  const isDark = props.isDark !== false;

  const filteredProjects = useMemo(
    () =>
      PROJECTS.filter(
        (repo) => filter === "all" || getProjectProfile(repo).id === filter
      ),
    [filter]
  );
  const selectedProject =
    filteredProjects.find((repo) => repo.id === selectedId) ||
    filteredProjects[0] ||
    PROJECTS.find((repo) => repo.id === selectedId) ||
    PROJECTS[0];
  const totalStars = PROJECTS.reduce(
    (sum, repo) => sum + (repo.stargazerCount || 0),
    0
  );
  const languageCount = new Set(
    PROJECTS.map((repo) => repo.primaryLanguage).filter(Boolean)
  ).size;

  useEffect(() => {
    const updateProgress = () => {
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(
        scrollable > 0 ? Math.min(1, window.pageYOffset / scrollable) : 0
      );
    };
    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);
    return () => {
      window.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
    };
  }, []);

  useEffect(() => {
    if (
      filteredProjects.length &&
      !filteredProjects.some((repo) => repo.id === selectedId)
    ) {
      setSelectedId(filteredProjects[0].id);
    }
  }, [filteredProjects, selectedId]);

  return (
    <div
      className={`project-observatory ${
        isDark ? "project-observatory-dark" : "project-observatory-light"
      }`}
    >
      <div className="project-noise" aria-hidden="true"></div>
      <div className="project-grid" aria-hidden="true"></div>
      <div className="project-progress" aria-hidden="true">
        <span style={{ transform: `scaleX(${scrollProgress})` }}></span>
      </div>
      <Header theme={props.theme} onToggle={props.onToggle} isDark={isDark} />

      <main>
        <section className="archive-hero" id="archive">
          <div className="archive-hero-meta archive-meta-left">
            <i></i> ARCHIVE / {new Date().getFullYear()}
          </div>
          <div className="archive-hero-meta archive-meta-right">
            NEW YORK / REMOTE <span>•</span> OPEN SOURCE LOG
          </div>
          <div className="archive-hero-copy">
            <p className="archive-eyebrow">
              <span></span> Project archive
            </p>
            <h1>
              Systems with
              <br />
              <em>receipts.</em>
            </h1>
            <p>{projectsHeader.description}</p>
            <div className="archive-hero-actions">
              <a className="archive-button-primary" href="#project-feed">
                Explore projects <Arrow />
              </a>
              <Link
                className="archive-button-secondary"
                to="/contact"
                aria-label="Discuss an opportunity with Sethuram"
              >
                Discuss an opportunity <Arrow />
              </Link>
            </div>
          </div>
          <div
            className="archive-radar-stage"
            aria-label="Interactive project radar"
          >
            <div className="radar-corner radar-corner-tl"></div>
            <div className="radar-corner radar-corner-tr"></div>
            <div className="radar-corner radar-corner-bl"></div>
            <div className="radar-corner radar-corner-br"></div>
            <div className="radar-meta radar-meta-top">
              <span>REPOSITORY ARRAY</span>
              <span>LIVE</span>
            </div>
            <div className="archive-radar">
              <span className="radar-sweep"></span>
              <span className="radar-ring radar-ring-one"></span>
              <span className="radar-ring radar-ring-two"></span>
              <span className="radar-ring radar-ring-three"></span>
              <i className="radar-axis radar-axis-x"></i>
              <i className="radar-axis radar-axis-y"></i>
              <button
                type="button"
                className="radar-pip radar-pip-cyan"
                onClick={() => setSelectedId(PROJECTS[0] && PROJECTS[0].id)}
                aria-label="Inspect first agent system"
              ></button>
              <button
                type="button"
                className="radar-pip radar-pip-violet"
                onClick={() => setSelectedId(PROJECTS[2] && PROJECTS[2].id)}
                aria-label="Inspect interface project"
              ></button>
              <button
                type="button"
                className="radar-pip radar-pip-orange"
                onClick={() => setSelectedId(PROJECTS[5] && PROJECTS[5].id)}
                aria-label="Inspect data system"
              ></button>
              <button
                type="button"
                className="radar-pip radar-pip-green"
                onClick={() => setSelectedId(PROJECTS[8] && PROJECTS[8].id)}
                aria-label="Inspect lab project"
              ></button>
              <span className="radar-center">
                SG<i></i>
              </span>
            </div>
            <div className="radar-bottom">
              <span>SCAN / SELECT A SIGNAL</span>
              <span>11 CHANNELS</span>
            </div>
          </div>
          <div className="archive-hero-status">
            <span>
              <i></i> ARCHIVE STATUS / SYNCHRONIZED
            </span>
            <span>
              {PROJECTS.length} REPOSITORIES · {totalStars} STARS ·{" "}
              {languageCount} LANGUAGES
            </span>
          </div>
        </section>

        <section
          className="archive-stats"
          aria-label="Project archive highlights"
        >
          <div>
            <b>{String(PROJECTS.length).padStart(2, "0")}</b>
            <span>
              repositories
              <br />
              in the field
            </span>
          </div>
          <div>
            <b>{String(totalStars).padStart(2, "0")}</b>
            <span>
              GitHub stars
              <br />
              across projects
            </span>
          </div>
          <div>
            <b>04</b>
            <span>
              operating modes
              <br />
              represented
            </span>
          </div>
          <div>
            <b>{String(languageCount).padStart(2, "0")}</b>
            <span>
              primary languages
              <br />
              in rotation
            </span>
          </div>
        </section>

        <section className="archive-workspace project-shell" id="project-feed">
          <div className="archive-section-heading">
            <div>
              <p className="archive-eyebrow">
                <span></span> Project feed
              </p>
              <h2>
                Find the signal
                <br />
                <em>behind the build.</em>
              </h2>
            </div>
            <p>
              Every card opens a live project brief. Filter the archive, inspect
              the work, then follow the source to see how it was made.
            </p>
          </div>

          <div className="archive-control-bar">
            <span className="control-bar-label">FILTER BY SYSTEM</span>
            <div
              className="archive-filters"
              role="tablist"
              aria-label="Project categories"
            >
              {FILTERS.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={filter === item.id ? "is-selected" : ""}
                  onClick={() => setFilter(item.id)}
                  role="tab"
                  aria-selected={filter === item.id}
                >
                  {item.label}
                  <span>
                    {item.id === "all"
                      ? PROJECTS.length
                      : PROJECTS.filter(
                          (repo) => getProjectProfile(repo).id === item.id
                        ).length}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="archive-feed-layout">
            <div className="archive-project-grid">
              {filteredProjects.map((repo, index) => (
                <ProjectCard
                  key={repo.id}
                  repo={repo}
                  index={index}
                  active={selectedProject && repo.id === selectedProject.id}
                  onSelect={(project) => setSelectedId(project.id)}
                />
              ))}
              {!filteredProjects.length && (
                <p className="archive-empty-state">
                  No signals found in this channel yet.
                </p>
              )}
            </div>
            {selectedProject && <ProjectInspector repo={selectedProject} />}
          </div>
        </section>

        <section className="research-vault project-shell" id="research">
          <div className="archive-section-heading research-heading">
            <div>
              <p className="archive-eyebrow">
                <span></span> Research vault
              </p>
              <h2>
                Ideas that made
                <br />
                <em>it past the prototype.</em>
              </h2>
            </div>
            <p>
              {publicationsHeader.description} The archive holds both the work
              that shipped as code and the work that earned a record of its own.
            </p>
          </div>
          <div className="research-record-grid">
            {publications.data.map((publication, index) => (
              <ResearchCard
                key={publication.id}
                publication={publication}
                index={index}
              />
            ))}
          </div>
        </section>

        <section className="archive-handoff">
          <div
            className="handoff-orbit handoff-orbit-one"
            aria-hidden="true"
          ></div>
          <div
            className="handoff-orbit handoff-orbit-two"
            aria-hidden="true"
          ></div>
          <div className="archive-handoff-inner project-shell">
            <div>
              <p className="archive-eyebrow">
                <span></span> Source available
              </p>
              <h2>
                The best proof is
                <br />
                <em>the work itself.</em>
              </h2>
            </div>
            <div className="handoff-console">
              <div className="handoff-console-top">
                <span>
                  <i></i>
                  <i></i>
                  <i></i>
                </span>
                <b>archive.log</b>
                <span>•••</span>
              </div>
              <div className="handoff-console-body">
                <p>
                  <span>$</span> source --status
                </p>
                <p className="handoff-ok">✓ public repositories available</p>
                <p>
                  <span>$</span> inspect --next
                </p>
                <a
                  href={greeting.githubProfile}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  github.com/Sethuram2003 <Arrow diagonal />
                </a>
                <div className="handoff-cursor">
                  <span>$</span>
                  <i></i>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer theme={props.theme} onToggle={props.onToggle} isDark={isDark} />
    </div>
  );
}
